// GLBQ reader — PURE module, the ONE reader of the building tile GLBs
// (buildings/<tx>_<ty>.glb, APP_REARCH §5 R0). Shared by the client (esbuild
// import: client/src/buildings.js), the node scripts / stages (require:
// groundm/bakectx.mjs, scripts/check-*.mjs) and python (pipeline/glbq.py
// spawns `node pipeline/glbpack.mjs read`). Dependency-free like ./spans.js:
// the meshopt decoder is INJECTED (three's
// examples/jsm/libs/meshopt_decoder.module.js MeshoptDecoder).
//
// GLBQ (pipeline/glbpack.mjs writes it; it is the format's one definition):
// one mesh, one indexed triangle primitive, every bufferView
// EXT_meshopt_compression (buffer 0 = the BIN chunk, buffer 1 = the
// fallback), KHR_mesh_quantization:
//   POSITION   u16 VEC3 (stride 8), world = node.translation + i * node.scale
//   NORMAL     i8 normalized VEC3 (stride 4), n = max(-1, i / 127)
//   COLOR_0    f32 VEC3   TEXCOORD_0 f32 VEC2   TEXCOORD_1 f32 VEC2 (both or none)
//   indices    u16 / u32, the soup's triangle order (registry `draw` ranges and
//              the client's cell runs index triangles of the soup)
// Strict: anything else throws (no float fallback).
//
// readGlbSoup(u8, decoder) -> { ntri, position, normal, color, uv0|null,
//   uv1|null, q, t }: world-space NON-indexed Float32Arrays, triangle order
//   kept (the index codec may rotate a triangle's corners; winding is kept).
'use strict';

const MAGIC = 0x46546C67, CH_JSON = 0x4E4F534A, CH_BIN = 0x004E4942;
const EXT = 'EXT_meshopt_compression', QUANT = 'KHR_mesh_quantization';

function fail(msg) { throw new Error(`glbsoup: not GLBQ (${msg})`); }

// (JSON doc, BIN chunk bytes) of a GLB2
function parseGlb(u8) {
  if (!(u8 instanceof Uint8Array) || u8.byteLength < 20) fail('not a GLB byte array');
  const dv = new DataView(u8.buffer, u8.byteOffset, u8.byteLength);
  if (dv.getUint32(0, true) !== MAGIC || dv.getUint32(4, true) !== 2) fail('bad GLB magic / version');
  if (dv.getUint32(8, true) !== u8.byteLength) fail('GLB length field disagrees with the bytes');
  const jl = dv.getUint32(12, true);
  if (dv.getUint32(16, true) !== CH_JSON || 20 + jl + 8 > u8.byteLength) fail('no JSON chunk');
  let json;
  try { json = JSON.parse(new TextDecoder().decode(u8.subarray(20, 20 + jl))); } catch (e) { fail(`JSON chunk: ${e.message}`); }
  const bl = dv.getUint32(20 + jl, true);
  if (dv.getUint32(24 + jl, true) !== CH_BIN || 28 + jl + bl > u8.byteLength) fail('no BIN chunk');
  return { json, bin: u8.subarray(28 + jl, 28 + jl + bl) };
}

const ATTRS = {   // name -> [componentType, type, normalized, byteStride]
  POSITION: [5123, 'VEC3', false, 8], NORMAL: [5120, 'VEC3', true, 4], COLOR_0: [5126, 'VEC3', false, 12],
  TEXCOORD_0: [5126, 'VEC2', false, 8], TEXCOORD_1: [5126, 'VEC2', false, 8] };

// the checked plan of a GLBQ doc: [{ name, acc, ext }] streams + node transform
function planOf(json, bin) {
  const has = (l, x) => Array.isArray(l) && l.includes(x);
  if (!has(json.extensionsRequired, EXT) || !has(json.extensionsRequired, QUANT)) fail('extensionsRequired');
  const meshes = json.meshes || [], prims = meshes.length === 1 ? meshes[0].primitives || [] : [];
  if (prims.length !== 1) fail('not one mesh with one primitive');
  const pr = prims[0];
  if ((pr.mode === undefined ? 4 : pr.mode) !== 4 || pr.indices === undefined) fail('not indexed triangles');
  const node = (json.nodes || [])[0] || {};
  const t = node.translation, s = node.scale;
  if (node.mesh !== 0 || node.matrix || node.rotation || !Array.isArray(t) || t.length !== 3
      || !Array.isArray(s) || s.length !== 3 || s[0] !== s[1] || s[0] !== s[2] || !(s[0] > 0)) fail('node 0 transform');
  const names = Object.keys(pr.attributes || {}).sort();
  const uv = names.includes('TEXCOORD_0');
  const want = ['COLOR_0', 'NORMAL', 'POSITION', ...(uv ? ['TEXCOORD_0', 'TEXCOORD_1'] : [])];
  if (names.join() !== want.join()) fail(`attributes ${names.join()}`);
  const stream = (name, ai) => {
    const acc = (json.accessors || [])[ai], bv = acc && (json.bufferViews || [])[acc.bufferView];
    const ext = bv && bv.extensions && bv.extensions[EXT];
    if (!ext || ext.buffer !== 0 || (ext.filter && ext.filter !== 'NONE')) fail(`${name}: bufferView not meshopt (buffer 0, no filter)`);
    if ((acc.byteOffset || 0) !== 0 || ext.count !== acc.count || ext.byteOffset + ext.byteLength > bin.byteLength) fail(`${name}: bufferView layout`);
    return { name, acc, ext };
  };
  const streams = want.map((n) => {
    const st = stream(n, pr.attributes[n]), [ct, ty, nz, bs] = ATTRS[n];
    if (st.acc.componentType !== ct || st.acc.type !== ty || !!st.acc.normalized !== nz
        || st.ext.byteStride !== bs || st.ext.mode !== 'ATTRIBUTES') fail(`${n}: accessor / stream type`);
    return st;
  });
  const ix = stream('indices', pr.indices);
  const isz = ix.acc.componentType === 5123 ? 2 : ix.acc.componentType === 5125 ? 4 : 0;
  if (!isz || ix.acc.type !== 'SCALAR' || ix.ext.byteStride !== isz || ix.ext.mode !== 'TRIANGLES' || ix.acc.count % 3) fail('indices');
  const nv = streams[0].acc.count;
  if (streams.some((st) => st.acc.count !== nv)) fail('attribute counts differ');
  streams.push(ix);
  return { streams, uv, q: s[0], t: t.slice(), nv, isz };
}

const srcOf = (bin, ext) => bin.subarray(ext.byteOffset || 0, (ext.byteOffset || 0) + ext.byteLength);

// the soup from the decoded streams (Uint8Array each, the plan's order)
function soupOf(plan, dec) {
  const { nv, q, t, uv, isz } = plan;
  const ixd = dec[dec.length - 1];
  const idx = isz === 2 ? new Uint16Array(ixd.buffer, ixd.byteOffset, ixd.byteLength / 2)
    : new Uint32Array(ixd.buffer, ixd.byteOffset, ixd.byteLength / 4);
  const nc = idx.length, ntri = nc / 3;
  for (let k = 0; k < nc; k++) if (idx[k] >= nv) fail('index out of range');
  const view = (i, T) => new T(dec[i].buffer, dec[i].byteOffset, dec[i].byteLength / T.BYTES_PER_ELEMENT);
  // streams: COLOR_0, NORMAL, POSITION[, TEXCOORD_0, TEXCOORD_1] (sorted names)
  const c32 = view(0, Float32Array), n8 = view(1, Int8Array), p16 = view(2, Uint16Array);
  // per unique vertex in float64, then stored f32
  const P = new Float32Array(nv * 3), N = new Float32Array(nv * 3);
  for (let v = 0; v < nv; v++) {
    for (let a = 0; a < 3; a++) {
      P[v * 3 + a] = t[a] + p16[v * 4 + a] * q;
      N[v * 3 + a] = Math.max(-1, n8[v * 4 + a] / 127);
    }
  }
  const position = new Float32Array(nc * 3), normal = new Float32Array(nc * 3), color = new Float32Array(nc * 3);
  const u0 = uv ? view(3, Float32Array) : null, u1 = uv ? view(4, Float32Array) : null;
  const uv0 = uv ? new Float32Array(nc * 2) : null, uv1 = uv ? new Float32Array(nc * 2) : null;
  for (let k = 0; k < nc; k++) {
    const v = idx[k];
    position[k * 3] = P[v * 3]; position[k * 3 + 1] = P[v * 3 + 1]; position[k * 3 + 2] = P[v * 3 + 2];
    normal[k * 3] = N[v * 3]; normal[k * 3 + 1] = N[v * 3 + 1]; normal[k * 3 + 2] = N[v * 3 + 2];
    color[k * 3] = c32[v * 3]; color[k * 3 + 1] = c32[v * 3 + 1]; color[k * 3 + 2] = c32[v * 3 + 2];
    if (uv) {
      uv0[k * 2] = u0[v * 2]; uv0[k * 2 + 1] = u0[v * 2 + 1];
      uv1[k * 2] = u1[v * 2]; uv1[k * 2 + 1] = u1[v * 2 + 1];
    }
  }
  return { ntri, position, normal, color, uv0, uv1, q, t };
}

// async: three's MeshoptDecoder (decodeGltfBufferAsync — its workers when
// useWorkers ran — else decodeGltfBuffer after `ready`)
async function readGlbSoup(u8, decoder) {
  const { json, bin } = parseGlb(u8);
  const plan = planOf(json, bin);
  await decoder.ready;
  const dec = await Promise.all(plan.streams.map(({ ext }) => {
    if (decoder.decodeGltfBufferAsync) {
      return decoder.decodeGltfBufferAsync(ext.count, ext.byteStride, srcOf(bin, ext), ext.mode, ext.filter || 'NONE');
    }
    const out = new Uint8Array(ext.count * ext.byteStride);
    decoder.decodeGltfBuffer(out, ext.count, ext.byteStride, srcOf(bin, ext), ext.mode, ext.filter || 'NONE');
    return out;
  }));
  return soupOf(plan, dec);
}

// sync twin for callers that cannot await per file (the node scripts):
// the decoder's `ready` must have resolved (await decoder.ready once)
function readGlbSoupSync(u8, decoder) {
  const { json, bin } = parseGlb(u8);
  const plan = planOf(json, bin);
  const dec = plan.streams.map(({ ext }) => {
    const out = new Uint8Array(ext.count * ext.byteStride);
    decoder.decodeGltfBuffer(out, ext.count, ext.byteStride, srcOf(bin, ext), ext.mode, ext.filter || 'NONE');
    return out;
  });
  return soupOf(plan, dec);
}

module.exports = { parseGlb, readGlbSoup, readGlbSoupSync };
